import {
    isEffectivelyEmpty,
    TEXT_FORMAT_EXEMPT_KEYS,
    toComparableDate,
    isPastCalendarDate,
    isUnchangedSavedDate,
    deepTrimStrings,
} from '../utils/formHelpers';
import stepsConfig from '../../stepsConfig';
import { findDepartureDurationErrorsForForm } from '../utils/tripDates';
import { WIZARD_STEPS, WIZARD_STEP_CONFIG_KEYS } from '../constants';

// Recursively find missing required fields, including inside repeatable-form items.
export const findEmptyRequiredFields = (fieldsMap, data, prefix = '') => {
    let missing = [];
    const safeData = data || {};

    Object.entries(fieldsMap || {}).forEach(([key, field]) => {
        const errorKey = prefix ? `${prefix}.${key}` : key;

        if (field.type === 'repeatable-form') {
            const items = Array.isArray(safeData[key]) ? safeData[key] : [];
            const minItems = Number(field.minItems);
            const requiresItems = field.required === true || (Number.isFinite(minItems) && minItems > 0);
            const minRequired = Number.isFinite(minItems) && minItems > 0 ? minItems : 1;
            if (requiresItems && items.length < minRequired) {
                missing.push({
                    key: errorKey,
                    message:
                        field.emptyMessage
                        || `Please add at least one ${field.label || key}`,
                });
                return;
            }
            items.forEach((item, index) => {
                missing = missing.concat(
                    findEmptyRequiredFields(field.fields, item, `${errorKey}.${index}`)
                );
            });
            return;
        }

        if (field.type === 'key-highlights') {
            (safeData[key] || []).forEach((item, index) => {
                if (!item?.name?.trim()) {
                    missing.push({ key: `${errorKey}.${index}.name`, message: 'Highlight name is required' });
                }
                if (!item?.location?.trim()) {
                    missing.push({ key: `${errorKey}.${index}.location`, message: 'Highlight location is required' });
                }
                if (!item?.image) {
                    missing.push({ key: `${errorKey}.${index}.image`, message: 'Highlight image is required' });
                }
            });
            return;
        }

        if (field.type === 'addon-extension-schedule') {
            const days = Array.isArray(safeData[key]) ? safeData[key] : [];
            if (field.required && days.length < 1) {
                missing.push({
                    key: errorKey,
                    message: 'Enter the Number of Days (whole number greater than 0)',
                });
                return;
            }
            const lineItemFields = stepsConfig.addons?.lineItemFields || {};
            days.forEach((day, dayIndex) => {
                if (isEffectivelyEmpty(day?.day_title)) {
                    missing.push({
                        key: `${errorKey}.${dayIndex}.day_title`,
                        message: 'Day Title is required',
                    });
                }
                (day?.line_items || []).forEach((li, itemIndex) => {
                    Object.entries(lineItemFields).forEach(([liKey, liField]) => {
                        if (!liField.required) return;
                        if (isEffectivelyEmpty(li?.[liKey])) {
                            missing.push({
                                key: `${errorKey}.${dayIndex}.line_items.${itemIndex}.${liKey}`,
                                message: 'This field is required',
                            });
                        }
                    });
                });
            });
            return;
        }

        // Retrieve value
        const value = safeData[key];
        const isEmpty = isEffectivelyEmpty(value);

        // IF field is marked as required, and empty and not checkbox, raise required error.
        if (field.required && field.type !== 'checkbox' && isEmpty) {
            missing.push({ key: errorKey, message: 'This field is required' });
        }

        // walk into conditional subFields if currently visible
        const matchedSub = field.subFields?.find(sub => {
            if (sub.showWhen === true) return value === true;
            if (sub.showWhen === '__has_value__') {
                return Array.isArray(value) ? value.length > 0 : !!value;
            }
            return sub.showWhen === value;
        });
        if (matchedSub) {
            // subField values live in the same data object as the parent, same prefix
            missing = missing.concat(findEmptyRequiredFields(matchedSub.fields, safeData, prefix));
        }
    });

    return missing;
};

export const findFormatErrors = (fieldsMap, data, prefix = '') => {
    let formatErrors = [];
    const safeData = data || {};

    Object.entries(fieldsMap || {}).forEach(([key, field]) => {
        const errorKey = prefix ? `${prefix}.${key}` : key;
        const value = safeData[key];

        if (field.type === 'repeatable-form') {
            (safeData[key] || []).forEach((item, index) => {
                formatErrors = formatErrors.concat(
                    findFormatErrors(field.fields, item, `${errorKey}.${index}`)
                );
            });
            return;
        }

        if (
            field.type === 'text'
            && typeof value === 'string'
            && !TEXT_FORMAT_EXEMPT_KEYS.has(key)
            && /\s{2,}/.test(value)
        ) {
            formatErrors.push({
                key: errorKey,
                message: 'Remove extra spaces from this field',
            });
        }

        const matchedSub = field.subFields?.find(sub => {
            if (sub.showWhen === true) return value === true;
            if (sub.showWhen === '__has_value__') {
                return Array.isArray(value) ? value.length > 0 : !!value;
            }
            return sub.showWhen === value;
        });
        if (matchedSub) {
            formatErrors = formatErrors.concat(findFormatErrors(matchedSub.fields, safeData, prefix));
        }
    });

    return formatErrors;
};

export const findItineraryErrors = (data) => {
    let errs = [];
    const days = data?.days || [];
    const lineItemFields = stepsConfig.itineraries?.lineItemFields || {};

    days.forEach((day, dayIndex) => {
        // ['day_title', 'description'].forEach((key) => {
        ['day_title'].forEach((key) => {
            const value = day[key];
            const isEmpty = isEffectivelyEmpty(value);
            if (isEmpty) {
                errs.push({ key: `days.${dayIndex}.${key}`, message: 'This field is required' });
            }
        });

        const lineItems = day.line_items || [];

        // Per-field checks (required + validateAfter etc.)
        lineItems.forEach((item, itemIndex) => {
            const itemPrefix = `days.${dayIndex}.line_items.${itemIndex}`;

            Object.entries(lineItemFields).forEach(([key, field]) => {
                const value = item[key];
                const isEmpty = isEffectivelyEmpty(value);
                const hasValue = !isEmpty;

                if (field.required && isEmpty) {
                    errs.push({ key: `${itemPrefix}.${key}`, message: 'This field is required' });
                }

                if (field.validateMinExclusive !== undefined && hasValue) {
                    if (parseFloat(value) <= field.validateMinExclusive) {
                        errs.push({
                            key: `${itemPrefix}.${key}`,
                            message: field.validateMinExclusiveMessage
                                || `Must be greater than ${field.validateMinExclusive}`,
                        });
                    }
                }

                if (field.validateMin !== undefined && hasValue) {
                    if (parseFloat(value) < field.validateMin) {
                        errs.push({
                            key: `${itemPrefix}.${key}`,
                            message: field.validateMinMessage || `Must be at least ${field.validateMin}`,
                        });
                    }
                }

                if (field.validateAfter && value && item[field.validateAfter]) {
                    if (toComparableDate(value) <= toComparableDate(item[field.validateAfter])) {
                        errs.push({
                            key: `${itemPrefix}.${key}`,
                            message: field.validateAfterMessage || 'Must be after the related field',
                        });
                    }
                }

                if (field.validateOnOrBefore && value && item[field.validateOnOrBefore]) {
                    if (toComparableDate(value) > toComparableDate(item[field.validateOnOrBefore])) {
                        errs.push({
                            key: `${itemPrefix}.${key}`,
                            message: field.validateOnOrBeforeMessage || 'Must not be later than the related field',
                        });
                    }
                }
            });
        });

        // NEW: sequence must be unique within this day's line items
        const sequenceCounts = {};
        lineItems.forEach((item) => {
            const seq = item.sequence;
            if (seq === undefined || seq === null || seq === '') return; // skip empty, required check already flags it
            sequenceCounts[seq] = (sequenceCounts[seq] || 0) + 1;
        });

        lineItems.forEach((item, itemIndex) => {
            const seq = item.sequence;
            if (seq === undefined || seq === null || seq === '') return;
            if (sequenceCounts[seq] > 1) {
                errs.push({
                    key: `days.${dayIndex}.line_items.${itemIndex}.sequence`,
                    message: 'Sequence must be unique within this day',
                });
            }
        });
    });

    return errs;
};

export const findDepartureDurationErrors = findDepartureDurationErrorsForForm;

export const findCrossFieldErrors = (fieldsMap, data, prefix = '') => {
    let errors = [];
    const safeData = data || {};

    Object.entries(fieldsMap || {}).forEach(([key, field]) => {
        const errorKey = prefix ? `${prefix}.${key}` : key;
        const value = safeData[key];
        const compareValue = field.validateGreaterThan ?? field.validateGreaterThanOrEqual
            ?? field.validateLessThan ?? field.validateLessThanOrEqual;
        const otherValue = compareValue ? safeData[compareValue] : undefined;

        const hasBothValues = !isEffectivelyEmpty(value) && !isEffectivelyEmpty(otherValue);

        const hasValue = !isEffectivelyEmpty(value);

        // --- Static (non-relative) bound checks ---

        // e.g. { validateMinExclusive: 0 } -> value must be strictly > 0
        if (field.validateMinExclusive !== undefined && hasValue) {
            if (parseFloat(value) <= field.validateMinExclusive) {
                errors.push({
                    key: errorKey,
                    message: field.validateMinExclusiveMessage
                        || `Must be greater than ${field.validateMinExclusive}`,
                });
            }
        }

        // e.g. { validateMin: 0 } -> value must be >= 0
        if (field.validateMin !== undefined && hasValue) {
            if (parseFloat(value) < field.validateMin) {
                errors.push({
                    key: errorKey,
                    message: field.validateMinMessage || `Must be at least ${field.validateMin}`,
                });
            }
        }

        // e.g. { validateMax: 100 } -> value must be <= 100
        if (field.validateMax !== undefined && hasValue) {
            if (parseFloat(value) > field.validateMax) {
                errors.push({
                    key: errorKey,
                    message: field.validateMaxMessage || `Must be no more than ${field.validateMax}`,
                });
            }
        }

        // e.g. { validatePhone: true } -> must look like a valid phone number
        if (field.validatePhone && hasValue) {
            // allows digits, spaces, +, -, () — 7 to 15 digits total (loose international format)
            const digitsOnly = String(value).replace(/\D/g, '');
            const phoneShapeRegex = /^\+?[0-9\s\-()]{7,20}$/;

            if (!phoneShapeRegex.test(value) || digitsOnly.length < 7 || digitsOnly.length > 15) {
                errors.push({
                    key: errorKey,
                    message: field.validatePhoneMessage || 'Enter a valid contact number',
                });
            }
        }

        if (
            field.disallowPast
            && hasValue
            && isPastCalendarDate(value)
            && !isUnchangedSavedDate(safeData, key, value)
        ) {
            errors.push({
                key: errorKey,
                message: field.disallowPastMessage || 'Date cannot be in the past',
            });
        }

        if (field.validateAfter && value && safeData[field.validateAfter]) {
            if (toComparableDate(value) <= toComparableDate(safeData[field.validateAfter])) {
                errors.push({ key: errorKey, message: field.validateAfterMessage || 'Must be after the related field' });
            }
        }

        if (field.validateOnOrAfter && value && safeData[field.validateOnOrAfter]) {
            if (toComparableDate(value) < toComparableDate(safeData[field.validateOnOrAfter])) {
                errors.push({
                    key: errorKey,
                    message: field.validateOnOrAfterMessage || 'Must not be earlier than the related field',
                });
            }
        }

        if (field.validateOnOrBefore && value && safeData[field.validateOnOrBefore]) {
            if (toComparableDate(value) > toComparableDate(safeData[field.validateOnOrBefore])) {
                errors.push({
                    key: errorKey,
                    message: field.validateOnOrBeforeMessage || 'Must not be later than the related field',
                });
            }
        }

        // Absolute calendar bounds (e.g. Dated season within selected departures).
        if (field.minFromField && value && safeData[field.minFromField]) {
            if (toComparableDate(value) < toComparableDate(safeData[field.minFromField])) {
                errors.push({
                    key: errorKey,
                    message: field.minFromFieldMessage || 'Must fall within the selected departure dates',
                });
            }
        }
        if (field.maxFromField && value && safeData[field.maxFromField]) {
            if (toComparableDate(value) > toComparableDate(safeData[field.maxFromField])) {
                errors.push({
                    key: errorKey,
                    message: field.maxFromFieldMessage || 'Must fall within the selected departure dates',
                });
            }
        }

        // strictly greater than
        if (field.validateGreaterThan && hasBothValues) {
            if (parseFloat(value) <= parseFloat(safeData[field.validateGreaterThan])) {
                errors.push({ key: errorKey, message: field.validateGreaterThanMessage || 'Must be greater than the related field' });
            }
        }

        // greater than or equal to
        if (field.validateGreaterThanOrEqual && hasBothValues) {
            if (parseFloat(value) < parseFloat(safeData[field.validateGreaterThanOrEqual])) {
                errors.push({ key: errorKey, message: field.validateGreaterThanOrEqualMessage || 'Must be greater than or equal to the related field' });
            }
        }

        // strictly less than
        if (field.validateLessThan && hasBothValues) {
            if (parseFloat(value) >= parseFloat(safeData[field.validateLessThan])) {
                errors.push({ key: errorKey, message: field.validateLessThanMessage || 'Must be less than the related field' });
            }
        }

        // less than or equal to
        if (field.validateLessThanOrEqual && hasBothValues) {
            if (parseFloat(value) > parseFloat(safeData[field.validateLessThanOrEqual])) {
                errors.push({ key: errorKey, message: field.validateLessThanOrEqualMessage || 'Must be less than or equal to the related field' });
            }
        }

        if (field.type === 'repeatable-form') {
            (safeData[key] || []).forEach((item, index) => {
                errors = errors.concat(findCrossFieldErrors(field.fields, item, `${errorKey}.${index}`));
            });
            return;
        }

        const matchedSub = field.subFields?.find(sub => {
            if (sub.showWhen === true) return value === true;
            if (sub.showWhen === '__has_value__') {
                return Array.isArray(value) ? value.length > 0 : !!value;
            }
            return sub.showWhen === value;
        });
        if (matchedSub) {
            errors = errors.concat(findCrossFieldErrors(matchedSub.fields, safeData, prefix));
        }
    });

    return errors;
};

export const validateWizardStep = ({ stepKey, stepConfig, formData }) => {
    const trimmedFormData = deepTrimStrings(formData);

    // Retrieve only current step fields for validation.
    const stepData = trimmedFormData[stepKey];
    const emptyFields = findEmptyRequiredFields(stepConfig.fields, stepData);
    const formatErrors = findFormatErrors(stepConfig.fields, stepData);
    const crossFieldErrors = findCrossFieldErrors(stepConfig.fields, stepData);
    const itineraryErrors = stepKey === 'itineraries' ? findItineraryErrors(stepData) : [];
    const departureDurationErrors = stepKey === 'departures'
        ? findDepartureDurationErrors(stepData, trimmedFormData.packages?.duration)
        : [];
    const allErrors = [
        ...emptyFields,
        ...formatErrors,
        ...crossFieldErrors,
        ...itineraryErrors,
        ...departureDurationErrors,
    ];

    return { errors: allErrors, trimmedFormData };
};

export const validateAllWizardSteps = (formData) => {
    const trimmedFormData = deepTrimStrings(formData);
    const errorsByStep = {};
    let firstInvalidStepIndex = -1;
    let firstInvalidStepKey = null;
    let firstInvalidStepLabel = null;
    const allErrors = [];

    WIZARD_STEP_CONFIG_KEYS.forEach((stepKey, index) => {
        if (stepKey === 'review') return;

        const stepConfig = stepsConfig[stepKey];
        if (!stepConfig?.fields) return;

        const { errors } = validateWizardStep({
            stepKey,
            stepConfig,
            formData: trimmedFormData,
        });

        if (errors.length > 0) {
            errorsByStep[stepKey] = errors;
            allErrors.push(...errors);
            if (firstInvalidStepIndex < 0) {
                firstInvalidStepIndex = index;
                firstInvalidStepKey = stepKey;
                firstInvalidStepLabel = WIZARD_STEPS[index] || stepKey;
            }
        }
    });

    return {
        isValid: firstInvalidStepIndex < 0,
        errors: allErrors,
        errorsByStep,
        firstInvalidStepIndex,
        firstInvalidStepKey,
        firstInvalidStepLabel,
    };
};

/** Steps the user may navigate to without using Continue (consecutive valid steps from the start). */
export const computeInitialCompletedSteps = (formData) => {
    const trimmedFormData = deepTrimStrings(formData);
    const completed = new Set();

    for (let index = 0; index < WIZARD_STEP_CONFIG_KEYS.length - 1; index += 1) {
        const stepKey = WIZARD_STEP_CONFIG_KEYS[index];
        const stepConfig = stepsConfig[stepKey];
        if (!stepConfig?.fields) {
            completed.add(index);
            continue;
        }

        const { errors } = validateWizardStep({
            stepKey,
            stepConfig,
            formData: trimmedFormData,
        });

        if (errors.length > 0) break;
        completed.add(index);
    }

    return completed;
};
